import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Swords, Shield, Target, TrendingUp, Users, Trophy } from 'lucide-react';
import { analyticsService } from '@/services/analyticsService';

interface CompetitiveMetrics {
  ranking: {
    position: number;
    totalTeams: number;
    trend: 'up' | 'down' | 'neutral';
  };
  strengthRating: number;
  recentForm: string[];
  headToHead: Array<{
    opponent: string;
    wins: number;
    losses: number;
    draws: number;
  }>;
  performanceComparison: Array<{
    stat: string;
    value: number;
    max: number;
  }>;
  seasonTrend: Array<{
    month: string;
    ranking: number;
  }>;
}

const CompetitiveAnalysis = () => {
  const [metrics, setMetrics] = useState<CompetitiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompetitiveMetrics();
  }, []);

  const fetchCompetitiveMetrics = async () => {
    try {
      const competitiveData = await analyticsService.getCompetitiveAnalysis();
      setMetrics(competitiveData as CompetitiveMetrics);
    } catch (error) {
      console.error('Error in fetchCompetitiveMetrics:', error);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-muted rounded w-1/3"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No competitive analysis data available yet</p>
            <p className="text-sm text-muted-foreground mt-2">Complete some games to see your competitive metrics</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">League Position</p>
                <p className="text-2xl font-bold">#{metrics.ranking.position}</p>
                <p className="text-xs text-muted-foreground">of {metrics.ranking.totalTeams} teams</p>
              </div>
              <Trophy className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Strength Rating</p>
                <p className="text-2xl font-bold">{metrics.strengthRating}/100</p>
                <Progress value={metrics.strengthRating} className="w-full mt-2" />
              </div>
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Recent Form</p>
                <div className="flex gap-1 mt-2">
                  {metrics.recentForm.map((result, index) => (
                    <Badge 
                      key={index}
                      variant={result === 'W' ? 'default' : result === 'L' ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {result}
                    </Badge>
                  ))}
                  {metrics.recentForm.length === 0 && (
                    <span className="text-sm text-muted-foreground">No recent games</span>
                  )}
                </div>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Comparison Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={metrics.performanceComparison}>
              <PolarGrid />
              <PolarAngleAxis dataKey="stat" />
              <PolarRadiusAxis domain={[0, 100]} />
              <Radar name="Performance" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
            </RadarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Season Ranking Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Season Ranking Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={metrics.seasonTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis reversed domain={[1, 'dataMax']} />
              <Tooltip />
              <Area type="monotone" dataKey="ranking" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Head-to-Head Records */}
      <Card>
        <CardHeader>
          <CardTitle>Head-to-Head Records</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.headToHead.map((record, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="font-medium">{record.opponent}</span>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">W: {record.wins}</span>
                  <span className="text-red-600">L: {record.losses}</span>
                  <span className="text-yellow-600">D: {record.draws}</span>
                </div>
              </div>
            ))}
            {metrics.headToHead.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No head-to-head records available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompetitiveAnalysis;